import "zod/compile";
import { z } from "zod";
import { workbookSourceSchema } from "./workbooks";

/**
 * A Data profile: statistics of a Source's or a Result's columns, and how
 * well two keys match, read without exporting rows, so a join can be tested
 * before it is built (a 1-character code meeting a 5-character one shows at
 * once). Only values' counts, a few of the most frequent values and a few
 * unmatched ones leave the Compute.
 */

/** The most rows a profile reads of each table; it says when it read fewer than the table has. */
export const WORKBOOK_PROFILE_MAX_ROWS = 1_000_000;
/** How many of a column's most frequent values, or a key's unmatched ones, a profile shows. */
export const WORKBOOK_PROFILE_TOP_VALUES = 10;
/** How many characters of a value a profile shows. */
export const WORKBOOK_PROFILE_VALUE_LENGTH = 100;

/** A table a profile reads: a Source as it would be read, or a Worksheet's Result of a completed Run. */
export const workbookProfileTableSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("source"), source: workbookSourceSchema }),
  z.object({
    kind: z.literal("result"),
    runId: z.uuid(),
    sheetId: z.uuid(),
  }),
]);
export type WorkbookProfileTable = z.infer<typeof workbookProfileTableSchema>;

const tableName = z.string().min(1).max(100);
const columnName = z.string().min(1).max(500);

/**
 * A key of a profiled table: one column, several (compared as their texts
 * joined with "|", in order, each one's own backslash and "|" escaped), or a Formula over its columns as a Worksheet
 * column's is written (`=UPPER(TRIM([@Code]))`).
 */
export const workbookProfileKeySchema = z
  .object({
    table: tableName,
    columns: z.array(columnName).min(1).max(5).optional(),
    formula: z.string().min(1).max(2000).optional(),
  })
  .refine((key) => Boolean(key.columns) !== Boolean(key.formula), {
    message: "Give a key's columns or its formula, not both.",
  });
export type WorkbookProfileKey = z.infer<typeof workbookProfileKeySchema>;

export const workbookProfileRequestSchema = z.object({
  /** The tables, each named for the columns and keys below to refer to. */
  tables: z
    .array(z.object({ name: tableName, table: workbookProfileTableSchema }))
    .min(1)
    .max(4),
  /** The columns to profile. */
  columns: z
    .array(z.object({ table: tableName, column: columnName }))
    .max(40)
    .default([]),
  /** Keys to match against each other, both ways. */
  pairs: z
    .array(
      z.object({
        left: workbookProfileKeySchema,
        right: workbookProfileKeySchema,
      }),
    )
    .max(5)
    .default([]),
  /** The most rows to read of each table. */
  maxRows: z
    .number()
    .int()
    .positive()
    .max(WORKBOOK_PROFILE_MAX_ROWS)
    .default(WORKBOOK_PROFILE_MAX_ROWS),
});
export type WorkbookProfileRequest = z.input<
  typeof workbookProfileRequestSchema
>;

const count = z.number().int().nonnegative();
const valueCount = z.object({ value: z.string(), count });

/** One column's statistics over the rows read. */
export const workbookColumnProfileSchema = z.object({
  table: z.string(),
  column: z.string(),
  rows: count,
  /** Empty cells: no value, or empty text. */
  blank: count,
  /** Error cells (a Result's #N/A, #VALUE!…). */
  errors: count,
  /** Distinct values among the other cells. */
  distinct: count,
  /** Whether no value appears twice (blanks and errors aside). */
  unique: z.boolean(),
  topValues: z.array(valueCount),
  /** The least and greatest value as text, in text order. */
  min: z.string().nullable(),
  max: z.string().nullable(),
  /** Lengths of the values, in characters. */
  length: z
    .object({ min: count, max: count, average: z.number().nonnegative() })
    .nullable(),
});
export type WorkbookColumnProfile = z.infer<typeof workbookColumnProfileSchema>;

/** How one side of a key pair meets the other. */
const keySideSchema = z.object({
  /** Distinct non-blank key values. */
  keys: count,
  /** Of those, how many the other side has. */
  matched: count,
  /** `matched / keys`, or null with no keys. */
  matchRate: z.number().min(0).max(1).nullable(),
  /** Key values appearing on more than one row: a Lookup on them would fail the Run. */
  duplicated: count,
  /** A few of the values the other side lacks, most frequent first. */
  unmatched: z.array(valueCount),
});

export const workbookKeyPairProfileSchema = z.object({
  left: keySideSchema,
  right: keySideSchema,
});
export type WorkbookKeyPairProfile = z.infer<
  typeof workbookKeyPairProfileSchema
>;

export const workbookProfileSchema = z.object({
  tables: z.array(
    z.object({
      name: z.string(),
      /** Rows read. */
      rows: count,
      /** Whether the table has more rows than were read. */
      truncated: z.boolean(),
    }),
  ),
  columns: z.array(workbookColumnProfileSchema),
  pairs: z.array(workbookKeyPairProfileSchema),
});
export type WorkbookProfile = z.infer<typeof workbookProfileSchema>;
